import { NavLink, useLocation } from 'react-router-dom'
import { ChevronDown, UsersRound } from 'lucide-react'
import { ADMIN_SEATS, PRODUCT_SEATS, SEAT_DOT, seatPath, type AgentSeat } from '@/lib/agent-seats'
import { OPS_NAV_ITEM, useNavSections } from '@/lib/nav'
import { formatRelativeTime } from '@/lib/utils'
import { cn } from '@/lib/utils'
import { useTeamActivity } from './useTeamActivity'

interface TeamSeatLinksProps {
  collapsed?: boolean
  isAdmin?: boolean
  onNavigate?: () => void
}

const TEAM_SECTION = 'Team'

function UnreadBadge({ count, testId }: { count: number; testId?: string }) {
  if (count <= 0) return null
  return (
    <span
      data-testid={testId}
      aria-label={`${count} unread`}
      className="ml-auto flex h-4 min-w-4 items-center justify-center rounded-full bg-brand-500 px-1 text-[10px] font-bold text-zinc-950"
    >
      {count > 99 ? '99+' : count}
    </span>
  )
}

function rowClass(collapsed: boolean) {
  return ({ isActive }: { isActive: boolean }) =>
    cn(
      'flex items-center gap-3 rounded-lg text-sm font-medium transition-colors duration-150 relative',
      collapsed ? 'justify-center p-2.5' : 'px-3 py-1.5',
      isActive
        ? 'text-brand-500 bg-brand-500/5'
        : 'text-zinc-400 hover:bg-zinc-800/50 hover:text-zinc-200',
    )
}

/**
 * Team seats (Scout, Analyst, …).
 *
 * The Ops row is a sibling rendered above the Team header, outside
 * the collapse toggle, so closing Team never hides the ops-room row
 * or its unread count. The header badge is only unread on seats that
 * are folded away. The seat you're currently on also stays visible
 * while closed.
 */
export default function TeamSeatLinks({ collapsed = false, isAdmin = false, onNavigate }: TeamSeatLinksProps) {
  const { pathname } = useLocation()
  const { activity, ops } = useTeamActivity()
  const { isOpen, toggle } = useNavSections(pathname)
  const open = isOpen(TEAM_SECTION, false)

  const seats: AgentSeat[] = isAdmin ? [...PRODUCT_SEATS, ...ADMIN_SEATS] : PRODUCT_SEATS
  const seatUnread = seats.reduce((sum, s) => sum + (activity[s.role]?.unread ?? 0), 0)

  const isCurrent = (to: string) => pathname === to
  const showSeat = (seat: AgentSeat) => open || isCurrent(seatPath(seat.role))

  const renderSeat = (seat: AgentSeat) => {
    const chip = activity[seat.role]
    const unread = chip?.unread ?? 0
    const when = chip?.lastActivityAt ? formatRelativeTime(chip.lastActivityAt) : undefined
    return (
      <NavLink
        key={seat.role}
        to={seatPath(seat.role)}
        end
        onClick={onNavigate}
        title={`${seat.label} — ${seat.description}${unread ? ` · ${unread} unread` : ''}${when ? ` · active ${when}` : ''}`}
        data-testid={`seat-${seat.role}`}
        className={rowClass(collapsed)}
      >
        {({ isActive }) => (
          <>
            {isActive && <div className="absolute left-0 top-1 bottom-1 w-0.5 rounded-r bg-brand-500" />}
            <span className="flex w-4 shrink-0 justify-center">
              <span className={cn('h-2 w-2 rounded-full', SEAT_DOT[seat.role])} />
            </span>
            {!collapsed && (
              <>
                <span className="min-w-0 flex-1 truncate">{seat.label}</span>
                {when && !unread && <span className="shrink-0 text-[10px] font-normal text-zinc-600">{when}</span>}
                <UnreadBadge count={unread} testId={`unread-${seat.role}`} />
              </>
            )}
            {collapsed && unread > 0 && (
              <span className="absolute right-1 top-1 h-1.5 w-1.5 rounded-full bg-brand-500" aria-label={`${unread} unread`} />
            )}
          </>
        )}
      </NavLink>
    )
  }

  const visibleAdminSeats = isAdmin ? ADMIN_SEATS.filter(showSeat) : []

  return (
    <div className="flex flex-col gap-0.5">
      {/* Sibling above the Team collapse — never a child of the toggle. */}
      <NavLink
        to={OPS_NAV_ITEM.to}
        end
        onClick={onNavigate}
        title={`${OPS_NAV_ITEM.label}${ops.unread ? ` · ${ops.unread} unread` : ''}`}
        data-testid="seat-ops"
        className={rowClass(collapsed)}
      >
        {({ isActive }) => (
          <>
            {isActive && <div className="absolute left-0 top-1 bottom-1 w-0.5 rounded-r bg-brand-500" />}
            <OPS_NAV_ITEM.icon className="h-4 w-4 shrink-0" />
            {!collapsed && (
              <>
                <span className="min-w-0 flex-1 truncate">{OPS_NAV_ITEM.label}</span>
                <UnreadBadge count={ops.unread} testId="unread-ops" />
              </>
            )}
            {collapsed && ops.unread > 0 && (
              <span className="absolute right-1 top-1 h-1.5 w-1.5 rounded-full bg-brand-500" aria-label={`${ops.unread} unread`} />
            )}
          </>
        )}
      </NavLink>

      <div data-testid="team-section">
        {/* Section header — doubles as the collapsed-state summary. */}
        <button
          type="button"
          onClick={() => toggle(TEAM_SECTION, false)}
          aria-expanded={open}
          aria-label={collapsed ? `Team${seatUnread ? ` (${seatUnread} unread)` : ''}` : undefined}
          title={collapsed ? `Team${seatUnread ? ` · ${seatUnread} unread` : ''}` : undefined}
          data-testid="team-toggle"
          className={cn(
            'group relative flex w-full items-center rounded-md transition-colors hover:bg-zinc-800/30',
            collapsed ? 'justify-center p-2 text-zinc-500 hover:text-zinc-300' : 'gap-2 px-3 py-1.5',
          )}
        >
          {collapsed ? (
            <>
              <UsersRound className="h-4 w-4" />
              {seatUnread > 0 && <span className="absolute right-1.5 top-1.5 h-1.5 w-1.5 rounded-full bg-brand-500" />}
            </>
          ) : (
            <>
              <span className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500">Team</span>
              {!open && (
                <span className="flex items-center -space-x-0.5" aria-hidden="true">
                  {PRODUCT_SEATS.map((seat) => (
                    <span
                      key={seat.role}
                      className={cn('h-1.5 w-1.5 rounded-full ring-1 ring-zinc-900', SEAT_DOT[seat.role])}
                    />
                  ))}
                </span>
              )}
              <span className="ml-auto flex items-center gap-1.5">
                {!open && <UnreadBadge count={seatUnread} testId="unread-team" />}
                <ChevronDown
                  className={cn('h-3 w-3 text-zinc-600 transition-transform duration-200', !open && '-rotate-90')}
                />
              </span>
            </>
          )}
        </button>

        <div className="flex flex-col gap-0.5" data-testid="team-seats">
          {PRODUCT_SEATS.filter(showSeat).map(renderSeat)}

          {visibleAdminSeats.length > 0 && (
            <>
              {!collapsed && open && (
                <span className="px-3 pt-2 pb-0.5 text-[10px] font-medium uppercase tracking-wider text-zinc-600">System</span>
              )}
              {visibleAdminSeats.map(renderSeat)}
            </>
          )}
        </div>
      </div>
    </div>
  )
}
